<!-- src/components/Inventario/Reportes/ScrapAlmacenLaser/components/SALGraficaPareto.vue -->
<template>
  <div class="sal-pareto-wrapper">
    <canvas v-show="datosFiltrados.length" ref="canvasRef"></canvas>
    <div v-if="!datosFiltrados.length" class="sal-pareto-empty">
      Sin defectos registrados para el periodo seleccionado
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { DIVISAN_COLORS } from "@/config/palette";

Chart.register(...registerables, ChartDataLabels);

const props = defineProps({
  // [{ codigo, descripcion, cantidad_scrap, porcentaje, porcentaje_acumulado }]
  datos: { type: Array, default: () => [] },
  subtitulo: { type: String, default: "" },
});

// Canvas/Chart.js no resuelve custom properties de CSS: usa la paleta en JS.
const COLOR_BARRA = "#7b2637";
const COLOR_LINEA = DIVISAN_COLORS.greenDark;
const COLOR_REF_80 = "#c0392b";

function formatearPiezas(v) {
  return new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 }).format(v ?? 0);
}

// Recalcula el % acumulado sobre los datos tal como llegan (ya filtrados
// por producto(s) del lado del backend).
const datosFiltrados = computed(() => {
  const base = props.datos;
  if (!base.length) return [];

  const total = base.reduce((s, d) => s + d.cantidad_scrap, 0);
  let acumulado = 0;
  return base.map((d) => {
    acumulado += d.cantidad_scrap;
    return {
      ...d,
      porcentaje: total ? +((d.cantidad_scrap / total) * 100).toFixed(2) : 0,
      porcentaje_acumulado: total ? +((acumulado / total) * 100).toFixed(2) : 0,
    };
  });
});

const canvasRef = ref(null);
let chartInstance = null;

function buildConfig() {
  const items = datosFiltrados.value;
  const labels = items.map((d) => d.descripcion);
  const cantidades = items.map((d) => d.cantidad_scrap);
  const acumulados = items.map((d) => d.porcentaje_acumulado);
  const linea80 = items.map(() => 80);

  return {
    type: "bar",
    data: {
      labels,
      datasets: [
        {
          type: "bar",
          label: "Piezas de scrap",
          data: cantidades,
          backgroundColor: COLOR_BARRA,
          yAxisID: "y",
          order: 2,
          datalabels: {
            display: (ctx) => (ctx.parsed?.y ?? 0) > 0,
            color: "#fff",
            anchor: "end",
            align: "start",
            font: { weight: "bold", size: 10 },
            formatter: (v) => formatearPiezas(v),
          },
        },
        {
          type: "line",
          label: "% Acumulado",
          data: acumulados,
          borderColor: COLOR_LINEA,
          backgroundColor: "transparent",
          pointBackgroundColor: COLOR_LINEA,
          borderWidth: 2,
          pointRadius: 4,
          tension: 0,
          yAxisID: "y_pct",
          order: 1,
          datalabels: {
            display: (ctx) => (ctx.parsed?.y ?? 0) > 0,
            color: COLOR_LINEA,
            backgroundColor: "rgba(255,255,255,0.85)",
            borderRadius: 4,
            borderColor: COLOR_LINEA,
            borderWidth: 1,
            padding: { top: 2, bottom: 2, left: 4, right: 4 },
            anchor: "end",
            align: "top",
            offset: 4,
            font: { weight: "700", size: 9 },
            formatter: (v) => `${v}%`,
          },
        },
        {
          type: "line",
          label: "Referencia 80%",
          data: linea80,
          borderColor: COLOR_REF_80,
          backgroundColor: "transparent",
          borderWidth: 1.5,
          borderDash: [6, 4],
          pointRadius: 0,
          yAxisID: "y_pct",
          order: 0,
          datalabels: { display: false },
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        title: {
          display: true,
          text: "Pareto de defectos — Scrap Almacén y Láser",
          font: { size: 14, weight: "bold" },
        },
        subtitle: {
          display: !!props.subtitulo,
          text: props.subtitulo,
          font: { size: 12 },
          padding: { bottom: 8 },
        },
        legend: {
          display: true,
          position: "bottom",
          labels: {
            usePointStyle: true,
            filter: (item) => item.text !== "Referencia 80%",
          },
        },
        tooltip: {
          callbacks: {
            title: (items) => {
              const idx = items[0].dataIndex;
              const d = datosFiltrados.value[idx];
              return d ? `Código ${d.codigo}` : items[0].label;
            },
            label: (ctx) => {
              if (ctx.dataset.label === "% Acumulado")
                return `% Acumulado: ${ctx.parsed.y}%`;
              if (ctx.dataset.label === "Referencia 80%") return null;
              return `Scrap: ${formatearPiezas(ctx.parsed.y)} pzas`;
            },
          },
        },
        datalabels: { display: false },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            maxRotation: 35,
            minRotation: 25,
            font: { size: 11, weight: "bold" },
          },
        },
        y: {
          beginAtZero: true,
          position: "left",
          title: { display: true, text: "Piezas" },
          ticks: {
            callback: (v) =>
              new Intl.NumberFormat("es-MX", { notation: "compact" }).format(v),
          },
        },
        y_pct: {
          beginAtZero: true,
          max: 100,
          position: "right",
          title: { display: true, text: "% Acumulado" },
          grid: { drawOnChartArea: false },
          ticks: { callback: (v) => `${v}%` },
        },
      },
    },
  };
}

function destroyChart() {
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }
}
function renderChart() {
  destroyChart();
  if (!canvasRef.value || !datosFiltrados.value.length) return;
  chartInstance = new Chart(canvasRef.value, buildConfig());
}

onMounted(() => nextTick(renderChart));
onBeforeUnmount(() => destroyChart());

// Reacciona a cambios en datos Y en la selección de defectos
watch(
  () => [datosFiltrados.value, props.subtitulo],
  () => {
    destroyChart();
    nextTick(renderChart);
  },
  { deep: true }
);
</script>

<style scoped>
.sal-pareto-wrapper {
  position: relative;
  width: 100%;
  height: 340px;
}
.sal-pareto-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 0.9rem;
}
</style>
